import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Mail, Pencil, Trash2, X } from 'lucide-react';
import { closedReason, dueOf, isWalkIn, sourceLabel, whyText } from '../../../lib/eventJourney';
import { groupEntries, journeyMeta, journeyTitle, kindOf, KINDS } from '../../../lib/eventActivityLog';
import { TONES } from '../../../lib/eventJourney';
import { GuestIcon, MoveToMenu, StageChip, cardSub, initialsOf, shortDate } from './guestUi';
import { attendanceText, rsvpText } from './GuestsList';

const lastTouchText = (card) => (card.last_outreach_at ? shortDate(card.last_outreach_at) : '—');

/**
 * One guest, in full: the stage with why they are in it (1013-100), the facts, the actions and ONE Journey
 * timeline across invite, RSVP, check-in and follow-up. Each Journey entry names who did it, or says
 * "Automatically" (OA-1). It reads `GET /events/{id}/history?contact_id=`. Portaled to `<body>` (#496): the
 * page root's fade-in transform would otherwise become this fixed panel's containing block.
 */
export default function GuestDrawer({
    card, columnKey, columns, phase, history, timeZone, readOnly, onClose, onRequestMove, onEmail, onRemove, onRetryHistory,
}) {
    const closeRef = useRef(null);
    useEffect(() => {
        closeRef.current?.focus();
        const onKey = (event) => { if (event.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    if (!card) return null;
    const column = columns.find((item) => item.key === columnKey) || { key: columnKey, label: columnKey };
    const due = dueOf(card, columnKey, phase, shortDate);
    const entries = groupEntries(history?.items || []);
    const facts = [
        ['RSVP', `${rsvpText(card, columnKey)}${card.reconfirm_requested_at ? ' · Asked to reconfirm' : ''}`],
        ['Attendance', phase === 'before' && !card.attendance_status?.match(/attended|no_show/) ? 'Event not held yet' : attendanceText(card, phase)],
        ['Contact Owner', card.owner_name || 'Unassigned'],
        ['Next step', due ? due.text : '—'],
        ['Source', isWalkIn(card) ? 'Walk-in' : sourceLabel(card)],
        ['Last touch', lastTouchText(card)],
    ];
    const action = 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2.5 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50';
    return createPortal(
        <>
            <div className="fixed inset-0 z-40 bg-black/20" aria-hidden="true" onClick={onClose} />
            <div role="dialog" aria-label={`${card.name} · guest details`} data-testid="guest-drawer" className="fixed inset-y-0 right-0 z-40 w-full max-w-md overflow-y-auto border-l border-stone-200 bg-white p-5 shadow-xl">
                <div className="flex items-start gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-stone-100 text-sm font-semibold text-stone-700" aria-hidden="true">{initialsOf(card.name)}</span>
                    <div className="min-w-0 flex-1">
                        <h2 className="truncate font-semibold text-stone-900">{card.name}</h2>
                        <p className="truncate text-xs text-stone-500">{cardSub(card)}</p>
                        <a href={`/contacts/${encodeURIComponent(card.contact_id)}`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="guest-drawer-open-contact">Open contact</a>
                    </div>
                    <button ref={closeRef} type="button" aria-label="Close" onClick={onClose} className="rounded-md p-1.5 text-stone-500 hover:bg-stone-100"><X className="h-4 w-4" aria-hidden="true" /></button>
                </div>

                <div className="mt-5 space-y-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Stage</span>
                    <div className="flex flex-wrap items-center gap-2">
                        <MoveToMenu columns={columns} current={columnKey} disabled={readOnly} label="Move to…" testId="guest-drawer-move" onPick={(target) => onRequestMove([card], target)}
                            trigger={({ toggle, disabled }) => <StageChip column={column} label={columnKey === 'closed' ? `Closed · ${closedReason(card)}` : column.label} onClick={toggle} disabled={disabled} data-testid="guest-drawer-stage" />} />
                        <span className="text-xs text-stone-500" data-testid="guest-drawer-why">{whyText(card, columnKey)}</span>
                    </div>
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3" data-testid="guest-drawer-facts">
                    {facts.map(([label, value]) => (
                        <div key={label} className="min-w-0"><dt className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">{label}</dt><dd className="truncate text-sm text-stone-800">{value}</dd></div>
                    ))}
                </dl>

                <div className="mt-4 flex flex-wrap gap-2">
                    <a className={action} href={`/contacts/${encodeURIComponent(card.contact_id)}`}><Pencil className="h-3.5 w-3.5" aria-hidden="true" />Log activity</a>
                    <button type="button" className={action} disabled={!card.email} title={card.email ? undefined : 'This contact has no email address'} onClick={() => onEmail(card)} data-testid="guest-drawer-email"><Mail className="h-3.5 w-3.5" aria-hidden="true" />Email</button>
                    <button type="button" className={`${action} text-red-700`} disabled={readOnly} onClick={() => onRemove(card)} data-testid="guest-drawer-remove"><Trash2 className="h-3.5 w-3.5" aria-hidden="true" />Remove from event</button>
                </div>

                <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Journey</h3>
                <ol className="mt-2 space-y-3" data-testid="guest-drawer-journey">
                    {history?.loading && <li><Loader2 className="h-4 w-4 animate-spin text-stone-400" aria-label="Loading journey" /></li>}
                    {!history?.loading && history?.error && (
                        <li className="text-sm text-red-700">{history.error} <button type="button" className="font-medium underline" onClick={onRetryHistory}>Retry</button></li>
                    )}
                    {!history?.loading && !history?.error && entries.length === 0 && <li className="text-sm text-stone-500">No event history yet.</li>}
                    {!history?.loading && entries.map((entry) => {
                        const kind = KINDS[kindOf(entry.first)];
                        const t = TONES[kind.tone];
                        return (
                            <li key={entry.key} data-testid="guest-journey-entry" className="flex items-start gap-2.5">
                                <span className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border" style={{ background: t.bg, color: t.fg, borderColor: t.bd }}><GuestIcon name={kind.icon} className="h-3.5 w-3.5" /></span>
                                <div className="min-w-0">
                                    <span data-testid="guest-journey-title" className="block text-sm font-medium text-stone-900">{journeyTitle(entry)}</span>
                                    <span className="block text-xs text-stone-500">{journeyMeta(entry, timeZone)}</span>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </>,
        document.body,
    );
}
